import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';
import useClipboard from 'react-use-clipboard';

import Spinner from '../Spinner';
import Button from '../Button';
import LinkIcon from '../../icons/LinkIcon';
import getConfig from '../../../util/config';
import useAlbumSlideshow from '../../../apps/pro/views/Album/AlbumSlideshow/hooks/useAlbumSlideshow';
import Angle from '../Angle';
import { ALBUM_SLIDESHOW_MODE } from '../../../constants/albums';
import { DISPLAY_MODE } from '../../grid/components/AlbumSlideshowGridViewer/AlbumSlideshowGridViewer';
import QRIcon from '../../icons/QRIcon';
import { isNativeAppWebView } from '../../../services/native-bridge/native-apps-interface';

const propTypes = {
  album: PropTypes.shape({
    hash_code: PropTypes.string,
  }),
  albumId: PropTypes.string.isRequired,
  designerOptions: PropTypes.shape({
    onPresent: PropTypes.func,
    onShare: PropTypes.func,
  }),
  displayMode: PropTypes.oneOf(Object.values(DISPLAY_MODE)),
};

const STEPS = {
  SHARE: 'SHARE',
  QR: 'QR',
};

const AlbumSlideshowShare = ({
  albumId,
  designerOptions,
  displayMode = DISPLAY_MODE.PRESENT,
}) => {
  const [step, setStep] = useState(STEPS.SHARE);
  const { slideshow, loading } = useAlbumSlideshow({ albumId });

  const shareUrl = `${getConfig().app.baseUrl}/albums/${albumId}/slideshow${
    displayMode === DISPLAY_MODE.PRESENT ? '?present=1' : ''
  }`;
  const [copied, setCopied] = useClipboard(shareUrl);
  const [copiedEmbed, setCopiedEmbed] = useClipboard(slideshow?.embed_code);
  const isNativeView = isNativeAppWebView();

  if (!slideshow || loading) {
    return (
      <div className="w-100 h-100 d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  const onQRCodeDownload = () => {
    const canvas = document.getElementById(`${slideshow.id}-qr`);
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${slideshow.id}-QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <div className="pb-3">
      {step === STEPS.SHARE && (
        <Fragment>
          <div className="d-flex mb-2">
            <Button
              onClick={setCopied}
              btnStyle="reset"
              className="slideshow-share-action-button mr-1"
            >
              <div>
                <LinkIcon color="#0d84fe" size={20} />
              </div>
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            {!isNativeView && (
              <Button
                onClick={() => setStep(STEPS.QR)}
                btnStyle="reset"
                className="slideshow-share-action-button"
              >
                <div>
                  <QRIcon color="#0d84fe" size={20} />
                </div>
                QR
              </Button>
            )}
          </div>
          <h4 className="mb-0">Embed in webpage</h4>
          <p className="text-light text-sm">
            Paste your slideshow&apos;s HTML embed code into your webpage.
          </p>
          <pre className="slideshow-share-embed-code-container">
            {slideshow.embed_code}
          </pre>
          <Button onClick={setCopiedEmbed} className="copy-embed-cta">
            {copiedEmbed ? 'Copied!' : `</> Copy HTML`}
          </Button>
          {designerOptions && (
            <Fragment>
              <hr />
              {designerOptions.onPresent && (
                <Button
                  btnBlock
                  className="copy-embed-cta"
                  onClick={designerOptions.onPresent}
                >
                  Play slideshow
                </Button>
              )}
              {designerOptions.onShare && (
                <Button
                  btnBlock
                  className="copy-embed-cta"
                  onClick={designerOptions.onShare}
                >
                  Invite others to add{' '}
                  {slideshow.mode === ALBUM_SLIDESHOW_MODE.ALL_VIDEOS
                    ? 'videos'
                    : 'photos'}
                </Button>
              )}
            </Fragment>
          )}
        </Fragment>
      )}

      {step === STEPS.QR && (
        <Fragment>
          <Button
            onClick={() => setStep(STEPS.SHARE)}
            btnStyle="link"
            className="mb-2 text-bold"
          >
            <Angle direction="left" className="mr-1" /> Back
          </Button>
          <div className="d-flex flex-column align-items-center">
            <h3 className="mb-4">Share QR Code</h3>
            <QRCode
              id={`${slideshow.id}-qr`}
              value={shareUrl}
              renderAs="canvas"
              size={200}
              className="mb-4"
            />
            <Button onClick={onQRCodeDownload}>Download PNG</Button>
          </div>
        </Fragment>
      )}
    </div>
  );
};

AlbumSlideshowShare.propTypes = propTypes;

export default AlbumSlideshowShare;
